// NotificationBell.jsx
// Displays unread count badge + dropdown list of creator notifications.
// Used in CreatorPage header and MobileNav Creator tab.

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fetchNotifications,
  fetchUnreadNotificationCount,
  markNotificationsRead,
  markOneNotificationRead,
} from '../lib/supabase';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)   return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const ICONS = { install: '⬇', review: '⭐', rating: '⭐' };

export default function NotificationBell({ compact = false }) {
  const { user } = useAuth();
  const [open,        setOpen]        = useState(false);
  const [unread,      setUnread]      = useState(0);
  const [notifs,      setNotifs]      = useState([]);
  const [loading,     setLoading]     = useState(false);
  const ref = useRef(null);

  // Poll unread count every 60s
  useEffect(() => {
    if (!user) return;
    const load = () => fetchUnreadNotificationCount(user.id).then(setUnread).catch(() => {});
    load();
    const interval = setInterval(load, 300000); // poll every 5 min instead of 1 min
    return () => clearInterval(interval);
  }, [user?.id]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setLoading(true);
    try {
      const data = await fetchNotifications(user.id);
      setNotifs(data);
      // Mark all read after opening
      if (unread > 0) {
        await markNotificationsRead(user.id);
        setUnread(0);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  if (!user) return null;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Bell button */}
      <button onClick={handleOpen} style={{
        position: 'relative',
        background: open ? 'var(--accent-dim)' : 'none',
        border: `0.5px solid ${open ? 'var(--accent-border)' : 'var(--border2)'}`,
        borderRadius: 8, padding: compact ? '4px 8px' : '6px 10px',
        cursor: 'pointer', color: open ? 'var(--accent)' : 'var(--muted)',
        fontFamily: 'var(--font)', fontSize: compact ? 14 : 16,
        display: 'flex', alignItems: 'center', gap: 5,
        transition: 'all 0.15s',
      }}>
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#e24b4a', color: '#fff',
            fontSize: 9, fontWeight: 700,
            minWidth: 16, height: 16, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', border: '1.5px solid var(--bg)',
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
        {!compact && <span style={{ fontSize: 12 }}>Notifications</span>}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: 320, maxHeight: 420,
          background: 'var(--surface)', border: '0.5px solid var(--border2)',
          borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          zIndex: 9999, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Notifications</span>
            {notifs.length > 0 && (
              <span style={{ fontSize: 11, color: 'var(--dim)' }}>{notifs.length} total</span>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--dim)', fontSize: 13 }}>
                Loading…
              </div>
            ) : notifs.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>No notifications yet</div>
                <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.5 }}>
                  You'll be notified when someone installs or reviews your skills.
                </div>
              </div>
            ) : (
              notifs.map(n => (
                <div key={n.id} style={{
                  display: 'flex', gap: 12, padding: '12px 16px',
                  borderBottom: '0.5px solid var(--border)',
                  background: n.read ? 'transparent' : 'var(--accent-dim)',
                  transition: 'background 0.15s',
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>
                    {ICONS[n.type] || '🔔'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: n.read ? 400 : 600, color: 'var(--text)', marginBottom: 2 }}>
                      {n.title}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4, marginBottom: 3 }}>
                      {n.body}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--dim)' }}>
                      {timeAgo(n.created_at)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
